import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from '@artstorefronts/ui';
import { validateWeights } from '@arthelper/shared/acquisition/strategy';
import { ACTIVATE_STRATEGIES, type Strategy } from './queries';
import { errorMessage, label } from './shared';

interface Choice {
  id: string;
  weight: string;
}

const SELECT_CLASS =
  'h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900 md:text-sm';

function versionOf(strategies: Strategy[], id: string): string {
  const found = strategies.find((s) => s.id === id);
  if (!found) {
    return '?';
  }
  return `v${found.version}`;
}

function joinVersions(versions: string[]): string {
  if (versions.length <= 1) {
    return versions.join('');
  }
  return `${versions.slice(0, -1).join(', ')} and ${versions[versions.length - 1]}`;
}

/** Plain-English summary of what the activation changes, for the confirmation modal. */
function describe(strategies: Strategy[], picks: Choice[]): string[] {
  // The confirm dialog's body renders while closed, before anything is picked.
  if (picks.length === 0) {
    return [];
  }
  const chosen = picks.map((p) => versionOf(strategies, p.id));
  const split =
    picks.length === 1
      ? `Every new visitor will get ${chosen[0]}.`
      : `New visitors will be split ${picks[0].weight}/${picks[1].weight} between ${chosen[0]} and ${chosen[1]}.`;

  const archived = strategies
    .filter((s) => s.status === 'active' && !picks.some((p) => p.id === s.id))
    .map((s) => `v${s.version}`);
  const archiveLine = archived.length === 0 ? null : `${joinVersions(archived)} will be archived.`;

  const drafts = strategies
    .filter((s) => s.status === 'draft' && picks.some((p) => p.id === s.id))
    .map((s) => `v${s.version}`);
  const freezeLine = drafts.length === 0 ? null : `${joinVersions(drafts)} will be frozen; edit later by cloning.`;

  return [
    split,
    archiveLine,
    freezeLine,
    "Signed-up members keep their strategy. Visitors who haven't signed up are re-assigned from the active strategies if theirs is archived.",
    'Preview links keep showing their strategy, but anyone who signs up through one gets an active strategy.',
  ].filter((line): line is string => line !== null);
}

/** Replace the running set with one strategy, or two at a weight split. */
export function ActivatePanel({ strategies, onActivated }: { strategies: Strategy[]; onActivated: () => void }) {
  const [first, setFirst] = useState<Choice>({ id: '', weight: '100' });
  const [second, setSecond] = useState<Choice>({ id: '', weight: '' });
  const [confirming, setConfirming] = useState(false);
  const [activate, { loading }] = useMutation(ACTIVATE_STRATEGIES);

  const picks = useMemo(() => [first, second].filter((p) => p.id !== ''), [first, second]);
  const duplicate = first.id !== '' && first.id === second.id;
  const weightError = picks.length === 0 ? null : validateWeights(picks.map((p) => Number(p.weight)));
  const error = duplicate ? 'Pick two different strategies.' : weightError;
  const ready = picks.length > 0 && error === null;

  const chooseFirst = (id: string) => {
    setFirst((prev) => ({ ...prev, id }));
    if (id === '' || id === second.id) {
      chooseSecond('');
    }
  };

  const chooseSecond = (id: string) => {
    if (id === '') {
      setSecond({ id: '', weight: '' });
      setFirst((prev) => ({ ...prev, weight: '100' }));
      return;
    }
    setSecond({ id, weight: second.id === '' ? '50' : second.weight });
    if (second.id === '') {
      setFirst((prev) => ({ ...prev, weight: '50' }));
    }
  };

  // With two picks, typing one weight sets the other so they keep adding to 100.
  const setFirstWeight = (weight: string) => {
    setFirst((prev) => ({ ...prev, weight }));
    if (second.id === '' || weight === '' || Number.isNaN(Number(weight))) {
      return;
    }
    setSecond((prev) => ({ ...prev, weight: String(100 - Number(weight)) }));
  };

  const setSecondWeight = (weight: string) => {
    setSecond((prev) => ({ ...prev, weight }));
    if (weight === '' || Number.isNaN(Number(weight))) {
      return;
    }
    setFirst((prev) => ({ ...prev, weight: String(100 - Number(weight)) }));
  };

  const handleConfirm = async () => {
    try {
      await activate({ variables: { input: picks.map((p) => ({ id: p.id, trafficWeight: Number(p.weight) })) } });
      toast.success('Running set updated.');
      setFirst({ id: '', weight: '100' });
      setSecond({ id: '', weight: '' });
      onActivated();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not activate.'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <section className="rounded-lg bg-white p-4 shadow md:p-6">
      <h2 className="text-lg font-semibold">Activate</h2>
      <p className="mt-1 text-sm text-gray-500">
        Pick one strategy, or two for an A/B test. This replaces whatever is running now. To stop a test, activate the
        baseline.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="activate-first">Strategy</Label>
          <select
            id="activate-first"
            className={SELECT_CLASS}
            value={first.id}
            onChange={(e) => chooseFirst(e.target.value)}
          >
            <option value="">Choose a strategy…</option>
            {strategies.map((s) => (
              <option key={s.id} value={s.id}>
                {label(s)} ({s.status})
              </option>
            ))}
          </select>
          <Label htmlFor="activate-first-weight" className="block text-xs text-gray-500">
            Traffic %
          </Label>
          <Input
            id="activate-first-weight"
            type="number"
            inputMode="numeric"
            min={1}
            max={100}
            className="h-11 text-base md:text-sm"
            value={first.weight}
            disabled={second.id === ''}
            onChange={(e) => setFirstWeight(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="activate-second">Second strategy (optional, for an A/B test)</Label>
          <select
            id="activate-second"
            className={SELECT_CLASS}
            value={second.id}
            disabled={first.id === ''}
            onChange={(e) => chooseSecond(e.target.value)}
          >
            <option value="">None — run one strategy</option>
            {strategies
              .filter((s) => s.id !== first.id)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {label(s)} ({s.status})
                </option>
              ))}
          </select>
          {second.id !== '' && (
            <>
              <Label htmlFor="activate-second-weight" className="block text-xs text-gray-500">
                Traffic %
              </Label>
              <Input
                id="activate-second-weight"
                type="number"
                inputMode="numeric"
                min={1}
                max={99}
                className="h-11 text-base md:text-sm"
                value={second.weight}
                onChange={(e) => setSecondWeight(e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          className="h-11 bg-gray-900 hover:bg-gray-800 md:h-9"
          disabled={!ready || loading}
          onClick={() => setConfirming(true)}
        >
          {loading ? 'Activating…' : 'Review activation'}
        </Button>
        {picks.length > 0 && error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change what new visitors get?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                {describe(strategies, picks).map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 md:h-9">Cancel</AlertDialogCancel>
            <AlertDialogAction className="h-11 md:h-9" onClick={() => void handleConfirm()}>
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
